import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { hotComics, categoryTags } from '@/mocks/comics';

export default function Category() {
  const [searchParams] = useSearchParams();
  const [activeTag, setActiveTag] = useState<string>(
    searchParams.get('tag') || 'all'
  );
  const [sortBy, setSortBy] = useState<string>(
    searchParams.get('sort') || 'latest'
  );

  const filteredComics = useMemo(() => {
    let results = [...hotComics];
    if (activeTag !== 'all') {
      results = results.filter((c) => c.tags.includes(activeTag));
    }
    if (sortBy === 'popular') {
      results.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      results.sort((a, b) => b.rating - a.rating);
    } else {
      results.sort(
        (a, b) =>
          new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
      );
    }
    return results;
  }, [activeTag, sortBy]);

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-20 pb-10 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-foreground-700 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-800 font-medium">全部分类</span>
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
              <div>
                <span className="text-accent-500 text-xs font-medium tracking-wide">
                  / 漫画分类
                </span>
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mt-2 leading-snug">
                  恋爱·动作·悬疑
                  <br />
                  全品类收录，精准定位你的心动作品
                </h1>
                <p className="text-foreground-500 text-xs mt-4">
                  · 共收录 {hotComics.length} 部韩漫
                </p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {categoryTags.slice(0, 4).map((tag) => (
                  <span
                    key={tag.key}
                    className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center"
                    aria-label={tag.label}
                  >
                    <i className={`${tag.icon} text-foreground-600 text-lg`}></i>
                  </span>
                ))}
                <span className="text-foreground-500 text-xs ml-1">
                  每日更新，全集免费
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-primary-500 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-block px-4 py-1.5 border border-background-50/30 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                  全部作品
                </span>

                <button
                  type="button"
                  onClick={() => setActiveTag('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 whitespace-nowrap ${
                    activeTag === 'all'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50/10 text-background-50/70 hover:text-background-50'
                  }`}
                >
                  全部
                </button>
                {categoryTags.map((tag) => (
                  <button
                    key={tag.key}
                    type="button"
                    onClick={() => setActiveTag(tag.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 whitespace-nowrap ${
                      activeTag === tag.key
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-50/10 text-background-50/70 hover:text-background-50'
                    }`}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                {[
                  { key: 'latest', label: '最新' },
                  { key: 'popular', label: '热门' },
                  { key: 'rating', label: '评分' },
                ].map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSortBy(s.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 whitespace-nowrap ${
                      sortBy === s.key
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-50/10 text-background-50/70 hover:text-background-50'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    className="group block"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
                      <img
                        src={comic.coverUrl}
                        alt={comic.title}
                        title={`${comic.title} - 韩漫网`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {comic.status === '连载中' && (
                        <span className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded">
                          {comic.status}
                        </span>
                      )}
                      {comic.status === '已完结' && (
                        <span className="absolute top-2 left-2 bg-background-700/80 text-background-50 text-xs px-2 py-0.5 rounded">
                          {comic.status}
                        </span>
                      )}
                    </div>
                    <div className="mt-3">
                      <h3 className="text-background-50 font-semibold text-sm md:text-base truncate group-hover:text-accent-400 transition-colors duration-200">
                        {comic.title}
                      </h3>
                      <p className="text-background-50/50 text-xs mt-1">
                        更新至第 {comic.chapterCount} 话 · {comic.updateDate}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-accent-400 text-xs">
                          ★ {comic.rating}
                        </span>
                        <span className="text-background-50/40 text-xs">
                          {comic.views >= 1000000
                            ? `${(comic.views / 1000000).toFixed(1)}M 阅读`
                            : `${(comic.views / 10000).toFixed(0)}万 阅读`}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-background-50/60 text-sm">
                  该分类暂无漫画，请选择其他分类
                </p>
              </div>
            )}

            <div className="mt-10 flex items-center justify-center gap-2">
              {[1, 2, 3, 4, 5].map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`w-8 h-8 rounded-full text-xs font-medium transition-colors duration-200 ${
                    page === 1
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50/10 text-background-50/70 hover:text-background-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                className="w-8 h-8 rounded-full bg-background-50/10 text-background-50/70 hover:text-background-50 flex items-center justify-center transition-colors duration-200"
              >
                <i className="ri-arrow-right-s-line text-sm"></i>
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: '韩漫分类大全 - 韩漫网',
            description: '恋爱·动作·悬疑全品类收录，精准定位你的下一部心动韩漫作品',
            url: 'http://bwpntxx.cn/category',
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫网',
              url: 'http://bwpntxx.cn/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'http://bwpntxx.cn/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '全部分类',
                  item: 'http://bwpntxx.cn/category',
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}